import React, { useState, useEffect, useCallback } from 'react';
import TopNavigation from './components/TopNavigation';
import LeftToolbar from './components/LeftToolbar';
import ToolsSidebar from './components/ToolsSidebar';
import ZoomControl from './components/ZoomControl';

const App = () => {
  const [allPanelsOpen, setAllPanelsOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [pinchAccumulator, setPinchAccumulator] = useState(0);
  const [selectedTool, setSelectedTool] = useState('select');
  const [canvasObjects, setCanvasObjects] = useState([]);
  const [clipboardObject, setClipboardObject] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentRect, setCurrentRect] = useState(null);
  const [currentEllipse, setCurrentEllipse] = useState(null);
  const [drawStartPoint, setDrawStartPoint] = useState(null);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [tempTextValue, setTempTextValue] = useState('');
  
  // Color and Stroke state for shapes
  const [fillColor, setFillColor] = useState('#FFFFFF');
  const [fillOpacity, setFillOpacity] = useState(1.0);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(1);
  const [strokeOpacity, setStrokeOpacity] = useState(1.0);
  const [strokeStyle, setStrokeStyle] = useState('solid');

  const zoomLevels = [25, 50, 75, 100, 125, 150, 200, 300, 400, 500];

  // Handle color/stroke changes from the sidebar
  const handleColorChange = (colorData) => {
    console.log('App.js handleColorChange received:', colorData);
    setFillColor(colorData.color);
    setFillOpacity(colorData.opacity);
    
    // If an object is selected, apply the color change to it
    if (selectedObjectId) {
      console.log('Applying color to selected object:', selectedObjectId);
      const newObjects = canvasObjects.map(obj => {
        if (obj.id === selectedObjectId) {
          const updatedObj = {
            ...obj,
            fill: colorData.color,
            fillOpacity: colorData.opacity
          };
          console.log('Updated object:', updatedObj);
          return updatedObj;
        }
        return obj;
      });
      setCanvasObjects(newObjects);
      saveToHistory(newObjects);
      console.log('Applied color to selected object:', colorData);
    } else {
      console.log('No object selected, just updating global color state');
    }
    
    console.log('Color changed:', colorData);
  };

  const handleStrokeChange = (strokeData) => {
    console.log('App.js handleStrokeChange received:', strokeData);
    setStrokeColor(strokeData.color);
    setStrokeWidth(strokeData.weight);
    setStrokeOpacity(strokeData.opacity);
    setStrokeStyle(strokeData.style || 'solid');
    
    // If an object is selected, apply the stroke change to it
    if (selectedObjectId) {
      console.log('Applying stroke to selected object:', selectedObjectId);
      const newObjects = canvasObjects.map(obj => {
        if (obj.id === selectedObjectId) {
          const updatedObj = {
            ...obj,
            stroke: strokeData.color,
            strokeWidth: strokeData.weight,
            strokeOpacity: strokeData.opacity,
            strokeStyle: strokeData.style || 'solid'
          };
          console.log('Updated object stroke:', updatedObj);
          return updatedObj;
        }
        return obj;
      });
      setCanvasObjects(newObjects);
      saveToHistory(newObjects);
      console.log('Applied stroke to selected object:', strokeData);
    } else {
      console.log('No object selected, just updating global stroke state');
    }
    
    console.log('Stroke changed:', strokeData);
  };

  const toggleAllPanels = () => {
    setAllPanelsOpen(!allPanelsOpen);
  };

  const handleZoomChange = (newZoom) => {
    setZoomLevel(newZoom);
    // Reset pan when zoom changes to keep canvas centered
    if (newZoom === 100) {
      setPanOffset({ x: 0, y: 0 });
    }
    console.log(`Canvas zoom changed to: ${newZoom}%`);
  };

  const zoomIn = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex < zoomLevels.length - 1) {
      handleZoomChange(zoomLevels[currentIndex + 1]);
    }
  };

  const zoomOut = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex > 0) {
      handleZoomChange(zoomLevels[currentIndex - 1]);
    }
  };

  const handleToolSelect = (toolId) => {
    setSelectedTool(toolId);
    
    // Clear all interaction states when switching tools
    setIsPanning(false);
    setIsDragging(false);
    setIsResizing(false);
    setIsDrawing(false);
    setCurrentRect(null);
    setCurrentEllipse(null);
    setResizeHandle(null);
    setResizeStart(null);
    setDragStart(null);
    setDrawStartPoint(null);
    setEditingTextId(null);
    setTempTextValue('');
    setLastPanPoint({ x: 0, y: 0 });
    
    // Clear selection when switching to non-select tools
    if (toolId !== 'select') {
      setSelectedObjectId(null);
    }
    
    console.log(`Selected tool: ${toolId}`);
  };

  // Save current state to history
  const saveToHistory = useCallback((newObjects) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newObjects]);
    
    // Limit history to 50 states
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(prev => prev + 1);
    }
    
    setHistory(newHistory);
    console.log('Saved to history. Index:', historyIndex + 1, 'Total:', newHistory.length);
  }, [history, historyIndex]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCanvasObjects([...history[newIndex]]);
      setSelectedObjectId(null); // Clear selection when undoing
      console.log('Undo - moved to history index:', newIndex);
    }
  }, [historyIndex, history]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCanvasObjects([...history[newIndex]]);
      setSelectedObjectId(null); // Clear selection when redoing
      console.log('Redo - moved to history index:', newIndex);
    }
  }, [historyIndex, history]);

  // Delete selected object
  const handleDelete = useCallback(() => {
    if (selectedObjectId) {
      const newObjects = canvasObjects.filter(obj => obj.id !== selectedObjectId);
      setCanvasObjects(newObjects);
      saveToHistory(newObjects);
      setSelectedObjectId(null);
      console.log('Deleted selected object:', selectedObjectId);
    }
  }, [selectedObjectId, canvasObjects, saveToHistory]);

  // Cut selected object
  const handleCut = useCallback(() => {
    if (selectedObjectId) {
      const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
      if (selectedObject) {
        setClipboardObject({ ...selectedObject });
        const newObjects = canvasObjects.filter(obj => obj.id !== selectedObjectId);
        setCanvasObjects(newObjects);
        saveToHistory(newObjects);
        setSelectedObjectId(null);
        console.log('Cut object:', selectedObject);
      }
    }
  }, [selectedObjectId, canvasObjects, saveToHistory]);

  // Copy selected object
  const handleCopy = useCallback(() => {
    if (selectedObjectId) {
      const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
      if (selectedObject) {
        setClipboardObject({ ...selectedObject });
        console.log('Copied object:', selectedObject);
      }
    }
  }, [selectedObjectId, canvasObjects]);

  // Paste object from clipboard
  const handlePaste = useCallback(() => {
    if (clipboardObject) {
      // Create a new object with a new ID and slightly offset position
      const newObject = {
        ...clipboardObject,
        id: Date.now(),
      };
      
      // Offset the position slightly
      if (newObject.type === 'rectangle') {
        newObject.x += 20;
        newObject.y += 20;
      } else if (newObject.type === 'ellipse') {
        newObject.cx += 20;
        newObject.cy += 20;
      } else if (newObject.type === 'text') {
        newObject.x += 20;
        newObject.y += 20;
      }
      
      const newObjects = [...canvasObjects, newObject];
      setCanvasObjects(newObjects);
      saveToHistory(newObjects);
      setSelectedObjectId(newObject.id);
      console.log('Pasted object:', newObject);
    }
  }, [clipboardObject, canvasObjects, saveToHistory]);

  // Function to check if a point is inside a rectangle
  const isPointInRectangle = (x, y, rect) => {
    return x >= rect.x && 
           x <= rect.x + rect.width && 
           y >= rect.y && 
           y <= rect.y + rect.height;
  };

  // Function to check if a point is inside an ellipse
  const isPointInEllipse = (x, y, ellipse) => {
    const centerX = ellipse.cx;
    const centerY = ellipse.cy;
    const radiusX = ellipse.rx;
    const radiusY = ellipse.ry;
    const dx = (x - centerX) / radiusX;
    const dy = (y - centerY) / radiusY;
    return (dx * dx + dy * dy) <= 1;
  };

  // Function to check if a point is inside a text object  
  const isPointInText = (x, y, text) => {
    // Simple bounding box check for text
    const fontSize = text.fontSize || 16;
    const textWidth = (text.content || 'Sample Text').length * fontSize * 0.6; // Rough estimate
    const textHeight = fontSize;
    
    return x >= text.x && 
           x <= text.x + textWidth && 
           y >= text.y - textHeight && 
           y <= text.y;
  };

  // Function to find which object was clicked
  const findClickedObject = (x, y) => {
    // Check objects in reverse order (top to bottom)
    for (let i = canvasObjects.length - 1; i >= 0; i--) {
      const obj = canvasObjects[i];
      if (obj.type === 'rectangle' && isPointInRectangle(x, y, obj)) {
        return obj;
      }
      if (obj.type === 'ellipse' && isPointInEllipse(x, y, obj)) {
        return obj;
      }
      if (obj.type === 'text' && isPointInText(x, y, obj)) {
        return obj;
      }
    }
    return null;
  };

  // Function to get cursor style based on resize handle
  const getCursorForHandle = (handle) => {
    switch (handle) {
      case 'top-left':
      case 'bottom-right':
        return 'nw-resize';
      case 'top-right':
      case 'bottom-left':
        return 'ne-resize';
      case 'top-center':
      case 'bottom-center':
        return 'ns-resize';
      case 'left-center':
      case 'right-center':
        return 'ew-resize';
      default:
        return 'default';
    }
  };

  // Function to get current cursor based on hover state
  const getCurrentCursor = () => {
    // Hand tool cursors - highest priority
    if (selectedTool === 'hand') {
      return isPanning ? 'grabbing' : 'grab';
    }
    
    // Always show crosshair for drawing tools
    if (selectedTool === 'rectangle') return 'crosshair';
    if (selectedTool === 'ellipse') return 'crosshair';
    if (selectedTool === 'text') return 'text';
    if (selectedTool === 'zoom') return 'zoom-in';
    
    // For select tool, show appropriate cursor based on interaction state
    if (selectedTool === 'select') {
      if (isResizing) return getCursorForHandle(resizeHandle);
      if (isDragging) return 'grabbing';
      if (isPanning) return 'grabbing';
      if (zoomLevel > 100) return 'grab';
      // Default select tool cursor
      return 'default';
    }
    
    return 'default';
  };

  // Function to check if a point is inside a resize handle
  const isPointInHandle = (x, y, handleX, handleY, size = 12) => {
    return x >= handleX && 
           x <= handleX + size && 
           y >= handleY && 
           y <= handleY + size;
  };

  // Function to find which resize handle was clicked
  const findClickedResizeHandle = (x, y, obj) => {
    if (!obj) return null;
    
    const handleSize = 12;
    const offset = handleSize / 2;
    
    let handles = {};
    
    if (obj.type === 'rectangle') {
      handles = {
        'top-left': { x: obj.x - offset, y: obj.y - offset },
        'top-right': { x: obj.x + obj.width - offset, y: obj.y - offset },
        'bottom-left': { x: obj.x - offset, y: obj.y + obj.height - offset },
        'bottom-right': { x: obj.x + obj.width - offset, y: obj.y + obj.height - offset },
        'top-center': { x: obj.x + obj.width / 2 - offset, y: obj.y - offset },
        'bottom-center': { x: obj.x + obj.width / 2 - offset, y: obj.y + obj.height - offset },
        'left-center': { x: obj.x - offset, y: obj.y + obj.height / 2 - offset },
        'right-center': { x: obj.x + obj.width - offset, y: obj.y + obj.height / 2 - offset }
      };
    } else if (obj.type === 'ellipse') {
      handles = {
        'top-left': { x: obj.cx - obj.rx - offset, y: obj.cy - obj.ry - offset },
        'top-right': { x: obj.cx + obj.rx - offset, y: obj.cy - obj.ry - offset },
        'bottom-left': { x: obj.cx - obj.rx - offset, y: obj.cy + obj.ry - offset },
        'bottom-right': { x: obj.cx + obj.rx - offset, y: obj.cy + obj.ry - offset },
        'top-center': { x: obj.cx - offset, y: obj.cy - obj.ry - offset },
        'bottom-center': { x: obj.cx - offset, y: obj.cy + obj.ry - offset },
        'left-center': { x: obj.cx - obj.rx - offset, y: obj.cy - offset },
        'right-center': { x: obj.cx + obj.rx - offset, y: obj.cy - offset }
      };
    }
    
    console.log('Checking handles for object:', obj);
    console.log('Handle positions:', handles);
    
    for (const [handleName, handle] of Object.entries(handles)) {
      console.log(`Checking ${handleName}: handle at (${handle.x}, ${handle.y}), click at (${x}, ${y})`);
      if (isPointInHandle(x, y, handle.x, handle.y, handleSize)) {
        console.log(`Found handle: ${handleName}`);
        return handleName;
      }
    }
    
    return null;
  };

  // Keyboard shortcuts for zoom and editing commands
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts if user is typing in an input field
      const activeElement = document.activeElement;
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.isContentEditable ||
        activeElement.hasAttribute('contenteditable')
      );
      
      if (isTyping) {
        console.log('Ignoring shortcut - user is typing in:', activeElement.tagName);
        return;
      }
      
      console.log('Key pressed:', e.key, 'ctrlKey:', e.ctrlKey, 'metaKey:', e.metaKey);
      
      // Handle editing shortcuts
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case '=':
          case '+':
            e.preventDefault();
            console.log('Zoom in triggered');
            zoomIn();
            break;
          case '-':
            e.preventDefault();
            console.log('Zoom out triggered');
            zoomOut();
            break;
          case 'z':
            if (e.shiftKey) {
              e.preventDefault();
              console.log('Redo triggered (Ctrl+Shift+Z)');
              handleRedo();
            } else {
              e.preventDefault();
              console.log('Undo triggered (Ctrl+Z)');
              handleUndo();
            }
            break;
          case 'y':
            e.preventDefault();
            console.log('Redo triggered (Ctrl+Y)');
            handleRedo();
            break;
          case 'x':
            e.preventDefault();
            console.log('Cut triggered');
            // Find and cut selected object
            if (selectedObjectId) {
              const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
              if (selectedObject) {
                setClipboardObject({ ...selectedObject });
                setCanvasObjects(prev => prev.filter(obj => obj.id !== selectedObjectId));
                setSelectedObjectId(null);
                console.log('Cut object:', selectedObject);
              }
            }
            break;
          case 'c':
            e.preventDefault();
            console.log('Copy triggered');
            // Find and copy selected object
            if (selectedObjectId) {
              const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
              if (selectedObject) {
                setClipboardObject({ ...selectedObject });
                console.log('Copied object:', selectedObject);
              }
            }
            break;
          case 'v':
            e.preventDefault();
            console.log('Paste triggered');
            // Paste from clipboard
            if (clipboardObject) {
              const newObject = {
                ...clipboardObject,
                id: Date.now(),
              };
              
              // Offset the position slightly
              if (newObject.type === 'rectangle') {
                newObject.x += 20;
                newObject.y += 20;
              } else if (newObject.type === 'ellipse') {
                newObject.cx += 20;
                newObject.cy += 20;
              } else if (newObject.type === 'text') {
                newObject.x += 20;
                newObject.y += 20;
              }
              
              setCanvasObjects(prev => [...prev, newObject]);
              setSelectedObjectId(newObject.id);
              console.log('Pasted object:', newObject);
            }
            break;
        }
      } else {
        // Handle non-modifier keys
        switch (e.key.toLowerCase()) {
          case 'delete':
          case 'backspace':
            e.preventDefault();
            console.log('Delete triggered');
            // Delete selected object
            if (selectedObjectId) {
              setCanvasObjects(prev => prev.filter(obj => obj.id !== selectedObjectId));
              setSelectedObjectId(null);
              console.log('Deleted selected object:', selectedObjectId);
            }
            break;
          case 'v':
            e.preventDefault();
            handleToolSelect('select');
            break;
          case 'h':
            e.preventDefault();
            handleToolSelect('hand');
            break;
          case 'z':
            e.preventDefault();
            handleToolSelect('zoom');
            break;
          case 't':
            e.preventDefault();
            handleToolSelect('text');
            break;
          case 'r':
            e.preventDefault();
            handleToolSelect('rectangle');
            break;
          case 'e':
            e.preventDefault();
            handleToolSelect('ellipse');
            break;
          case 'i':
            e.preventDefault();
            handleToolSelect('image');
            break;
          case 'p':
            e.preventDefault();
            handleToolSelect('pen');
            break;
          case 'l':
            e.preventDefault();
            handleToolSelect('line');
            break;
        }
      }
    };

    // Global wheel event listener to prevent browser zoom
    const handleGlobalWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    // Attach to document for global keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleGlobalWheel, { passive: false });
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleGlobalWheel);
    };
  }, [selectedObjectId, canvasObjects, clipboardObject, zoomLevel, handleUndo, handleRedo, saveToHistory]);

  // Calculate canvas transform
  const canvasScale = zoomLevel / 100;

  // Pan handling functions
  const handleMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const canvasScale = zoomLevel / 100;
    // Calculate coordinates relative to canvas center (0,0 at center)
    // Extend the range slightly beyond viewport bounds to allow true edge-to-edge drawing
    const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
    const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
    
    // Allow unlimited coordinate range for true edge-to-edge drawing
    const extendedX = x;
    const extendedY = y;
    
    console.log('Mouse down at:', { x: extendedX, y: extendedY, originalX: x, originalY: y, selectedTool, rectBounds: rect });
    
    // Handle hand tool first - it should work everywhere
    if (selectedTool === 'hand') {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      console.log('Hand tool panning started at:', { x: e.clientX, y: e.clientY });
      e.preventDefault();
      return;
    }
    
    // Debug: check if we have a selected object
    const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
    console.log('Currently selected object:', selectedObject);
    
    if (selectedTool === 'select') {
      // Handle selection and dragging
      const clickedObject = findClickedObject(extendedX, extendedY);
      
      // Check for double-click on text objects to start editing
      if (clickedObject && clickedObject.type === 'text' && clickedObject.id === selectedObjectId) {
        // This is a click on already selected text - could be double-click for editing
        // We'll handle this in the double-click event handler
      }
      console.log('Clicked object:', clickedObject);
      
      if (clickedObject && clickedObject.id === selectedObjectId) {
        // Check if clicking on a resize handle of the selected object
        const clickedHandle = findClickedResizeHandle(extendedX, extendedY, clickedObject);
        console.log('Clicked handle:', clickedHandle, 'at position:', extendedX, extendedY);
        
        if (clickedHandle) {
          // Start resizing - clear any other states first
          setIsDragging(false);
          setDragStart(null);
          setIsDrawing(false);
          setCurrentRect(null);
          setCurrentEllipse(null);
          setIsPanning(false);
          
          setIsResizing(true);
          setResizeHandle(clickedHandle);
          setResizeStart({ 
            x: extendedX, y: extendedY, 
            objX: clickedObject.type === 'rectangle' ? clickedObject.x : clickedObject.cx, 
            objY: clickedObject.type === 'rectangle' ? clickedObject.y : clickedObject.cy,
            objWidth: clickedObject.type === 'rectangle' ? clickedObject.width : clickedObject.rx * 2,
            objHeight: clickedObject.type === 'rectangle' ? clickedObject.height : clickedObject.ry * 2
          });
          console.log('Starting resize with handle:', clickedHandle);
        } else {
          // Start dragging the selected object - clear other states first
          setIsResizing(false);
          setResizeHandle(null);
          setResizeStart(null);
          setIsDrawing(false);
          setCurrentRect(null);
          setCurrentEllipse(null);
          
          setIsDragging(true);
          if (clickedObject.type === 'rectangle') {
            setDragStart({ x: extendedX, y: extendedY, objX: clickedObject.x, objY: clickedObject.y });
          } else if (clickedObject.type === 'ellipse') {
            setDragStart({ x: extendedX, y: extendedY, objX: clickedObject.cx, objY: clickedObject.cy });
          } else if (clickedObject.type === 'text') {
            setDragStart({ x: extendedX, y: extendedY, objX: clickedObject.x, objY: clickedObject.y });
          }
          console.log('Starting drag of selected object');
        }
      } else if (clickedObject) {
        // Select a different object - clear all states first
        setIsResizing(false);
        setResizeHandle(null);
        setResizeStart(null);
        setIsDrawing(false);
        setCurrentRect(null);
        setCurrentEllipse(null);
        setIsPanning(false);
        
        setSelectedObjectId(clickedObject.id);
        setIsDragging(true);
        if (clickedObject.type === 'rectangle') {
          setDragStart({ x: extendedX, y: extendedY, objX: clickedObject.x, objY: clickedObject.y });
        } else if (clickedObject.type === 'ellipse') {
          setDragStart({ x: extendedX, y: extendedY, objX: clickedObject.cx, objY: clickedObject.cy });
        } else if (clickedObject.type === 'text') {
          setDragStart({ x: extendedX, y: extendedY, objX: clickedObject.x, objY: clickedObject.y });
        }
        console.log('Selected and starting drag:', clickedObject);
      } else {
        // Clicked on empty space - deselect and clear all states but don't interfere with hand tool
        setSelectedObjectId(null);
        if (selectedTool !== 'hand') {
          setIsDragging(false);
          setDragStart(null);
          setIsResizing(false);
          setResizeHandle(null);
          setResizeStart(null);
          setIsDrawing(false);
          setCurrentRect(null);
          setCurrentEllipse(null);
          setIsPanning(false);
        }
        console.log('Deselected all objects');
      }
      e.preventDefault();
      return;
    }
    
    if (selectedTool === 'rectangle') {
      // Start drawing rectangle
      setIsDrawing(true);
      setDrawStartPoint({ x: extendedX, y: extendedY });
      const newRect = {
        id: Date.now(),
        type: 'rectangle',
        x: extendedX,
        y: extendedY,
        width: 0,
        height: 0,
        fill: fillColor,
        fillOpacity: fillOpacity,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeOpacity: strokeOpacity,
        strokeStyle: strokeStyle
      };
      setCurrentRect(newRect);
      console.log('Starting rectangle:', newRect);
      e.preventDefault();
      return;
    }
    
    if (selectedTool === 'ellipse') {
      // Start drawing ellipse
      setIsDrawing(true);
      setDrawStartPoint({ x: extendedX, y: extendedY });
      const newEllipse = {
        id: Date.now(),
        type: 'ellipse',
        cx: extendedX,
        cy: extendedY,
        rx: 0,
        ry: 0,
        fill: fillColor,
        fillOpacity: fillOpacity,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeOpacity: strokeOpacity,
        strokeStyle: strokeStyle
      };
      setCurrentEllipse(newEllipse);
      console.log('Starting ellipse:', newEllipse);
      e.preventDefault();
      return;
    }
    
    if (selectedTool === 'text') {
      // Create text object
      const newText = {
        id: Date.now(),
        type: 'text',
        x: extendedX,
        y: extendedY,
        content: 'Sample Text',
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        fill: 'black',
        textAnchor: 'start'
      };
      
      const newObjects = [...canvasObjects, newText];
      setCanvasObjects(newObjects);
      saveToHistory(newObjects);
      setSelectedObjectId(newText.id);
      setEditingTextId(newText.id);
      setTempTextValue(newText.content);
      console.log('Created text object:', newText);
      e.preventDefault();
      return;
    }
    
    // Only enable panning for select tool when zoomed in if no other interaction is happening
    if (zoomLevel > 100 && selectedTool === 'select' && !isDragging && !isResizing && !isDrawing) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    const isShiftPressed = e.shiftKey;
    
    // Handle hand tool panning first
    if (isPanning && selectedTool === 'hand') {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      console.log('Hand tool panning:', { deltaX, deltaY });
      e.preventDefault();
      return;
    }
    
    if (isResizing && selectedTool === 'select' && selectedObjectId && resizeStart && resizeHandle) {
      const rect = e.currentTarget.getBoundingClientRect();
      const canvasScale = zoomLevel / 100;
      const rawX = (e.clientX - rect.left - rect.width / 2) / canvasScale;
      const rawY = (e.clientY - rect.top - rect.height / 2) / canvasScale;
      const currentX = rawX;
      const currentY = rawY;
      
      // Calculate the resize delta
      const deltaX = currentX - resizeStart.x;
      const deltaY = currentY - resizeStart.y;
      
      // Apply different resize logic based on which handle was clicked
      setCanvasObjects(prev => prev.map(obj => {
        if (obj.id === selectedObjectId) {
          let newObj = { ...obj };
          
          if (obj.type === 'rectangle') {
            let newWidth = resizeStart.objWidth;
            let newHeight = resizeStart.objHeight;
            let newX = resizeStart.objX;
            let newY = resizeStart.objY;
            
            // Calculate new dimensions based on handle
            switch (resizeHandle) {
              case 'top-left':
                newX = resizeStart.objX + deltaX;
                newY = resizeStart.objY + deltaY;
                newWidth = resizeStart.objWidth - deltaX;
                newHeight = resizeStart.objHeight - deltaY;
                break;
              case 'top-right':
                newY = resizeStart.objY + deltaY;
                newWidth = resizeStart.objWidth + deltaX;
                newHeight = resizeStart.objHeight - deltaY;
                break;
              case 'bottom-left':
                newX = resizeStart.objX + deltaX;
                newWidth = resizeStart.objWidth - deltaX;
                newHeight = resizeStart.objHeight + deltaY;
                break;
              case 'bottom-right':
                newWidth = resizeStart.objWidth + deltaX;
                newHeight = resizeStart.objHeight + deltaY;
                break;
              case 'top-center':
                newY = resizeStart.objY + deltaY;
                newHeight = resizeStart.objHeight - deltaY;
                break;
              case 'bottom-center':
                newHeight = resizeStart.objHeight + deltaY;
                break;
              case 'left-center':
                newX = resizeStart.objX + deltaX;
                newWidth = resizeStart.objWidth - deltaX;
                break;
              case 'right-center':
                newWidth = resizeStart.objWidth + deltaX;
                break;
            }
            
            // Apply shift constraint for proportional resizing
            if (isShiftPressed) {
              // For corner handles, make it square by using the larger dimension
              if (['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(resizeHandle)) {
                const maxDimension = Math.max(Math.abs(newWidth), Math.abs(newHeight));
                const signWidth = newWidth >= 0 ? 1 : -1;
                const signHeight = newHeight >= 0 ? 1 : -1;
                
                newWidth = maxDimension * signWidth;
                newHeight = maxDimension * signHeight;
                
                // Adjust position for constrained resize
                if (resizeHandle === 'top-left') {
                  newX = resizeStart.objX + resizeStart.objWidth - newWidth;
                  newY = resizeStart.objY + resizeStart.objHeight - newHeight;
                } else if (resizeHandle === 'top-right') {
                  newY = resizeStart.objY + resizeStart.objHeight - newHeight;
                } else if (resizeHandle === 'bottom-left') {
                  newX = resizeStart.objX + resizeStart.objWidth - newWidth;
                }
              }
            }
            
            // Ensure minimum size for rectangles
            if (Math.abs(newWidth) < 1) newWidth = newWidth >= 0 ? 1 : -1;
            if (Math.abs(newHeight) < 1) newHeight = newHeight >= 0 ? 1 : -1;
            
            newObj.x = newX;
            newObj.y = newY;
            newObj.width = Math.abs(newWidth);
            newObj.height = Math.abs(newHeight);
            
          } else if (obj.type === 'ellipse') {
            // For ellipses, we resize by changing the radii
            let newWidth = resizeStart.objWidth;
            let newHeight = resizeStart.objHeight;
            let newCenterX = resizeStart.objX;
            let newCenterY = resizeStart.objY;
            
            switch (resizeHandle) {
              case 'top-left':
                newCenterX = resizeStart.objX + (deltaX / 2);
                newCenterY = resizeStart.objY + (deltaY / 2);
                newWidth = resizeStart.objWidth - deltaX;
                newHeight = resizeStart.objHeight - deltaY;
                break;
              case 'top-right':
                newCenterX = resizeStart.objX + (deltaX / 2);
                newCenterY = resizeStart.objY + (deltaY / 2);
                newWidth = resizeStart.objWidth + deltaX;
                newHeight = resizeStart.objHeight - deltaY;
                break;
              case 'bottom-left':
                newCenterX = resizeStart.objX + (deltaX / 2);
                newCenterY = resizeStart.objY + (deltaY / 2);
                newWidth = resizeStart.objWidth - deltaX;
                newHeight = resizeStart.objHeight + deltaY;
                break;
              case 'bottom-right':
                newCenterX = resizeStart.objX + (deltaX / 2);
                newCenterY = resizeStart.objY + (deltaY / 2);
                newWidth = resizeStart.objWidth + deltaX;
                newHeight = resizeStart.objHeight + deltaY;
                break;
              case 'top-center':
                newCenterY = resizeStart.objY + (deltaY / 2);
                newHeight = resizeStart.objHeight - deltaY;
                break;
              case 'bottom-center':
                newCenterY = resizeStart.objY + (deltaY / 2);
                newHeight = resizeStart.objHeight + deltaY;
                break;
              case 'left-center':
                newCenterX = resizeStart.objX + (deltaX / 2);
                newWidth = resizeStart.objWidth - deltaX;
                break;
              case 'right-center':
                newCenterX = resizeStart.objX + (deltaX / 2);
                newWidth = resizeStart.objWidth + deltaX;
                break;
            }
            
            // Apply shift constraint for proportional resizing (perfect circles)
            if (isShiftPressed) {
              // For corner handles, make it a perfect circle by using the larger dimension
              if (['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(resizeHandle)) {
                const maxDimension = Math.max(Math.abs(newWidth), Math.abs(newHeight));
                newWidth = newWidth >= 0 ? maxDimension : -maxDimension;
                newHeight = newHeight >= 0 ? maxDimension : -maxDimension;
              }
            }
            
            // Ensure minimum size for ellipses
            if (Math.abs(newWidth) < 2) newWidth = newWidth >= 0 ? 2 : -2;
            if (Math.abs(newHeight) < 2) newHeight = newHeight >= 0 ? 2 : -2;
            
            newObj.cx = newCenterX;
            newObj.cy = newCenterY;
            newObj.rx = Math.abs(newWidth) / 2;
            newObj.ry = Math.abs(newHeight) / 2;
          }
          
          return newObj;
        }
        return obj;
      }));
      
      e.preventDefault();
      return;
    }
    
    if (isDragging && selectedTool === 'select' && selectedObjectId && dragStart) {
      const rect = e.currentTarget.getBoundingClientRect();
      const canvasScale = zoomLevel / 100;
      const rawX = (e.clientX - rect.left - rect.width / 2) / canvasScale;
      const rawY = (e.clientY - rect.top - rect.height / 2) / canvasScale;
      const currentX = rawX;
      const currentY = rawY;
      
      // Calculate the drag offset
      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;
      
      // Update the position of the selected object
      setCanvasObjects(prev => prev.map(obj => {
        if (obj.id === selectedObjectId) {
          if (obj.type === 'rectangle') {
            return {
              ...obj,
              x: dragStart.objX + deltaX,
              y: dragStart.objY + deltaY
            };
          } else if (obj.type === 'ellipse') {
            return {
              ...obj,
              cx: dragStart.objX + deltaX,
              cy: dragStart.objY + deltaY
            };
          } else if (obj.type === 'text') {
            return {
              ...obj,
              x: dragStart.objX + deltaX,
              y: dragStart.objY + deltaY
            };
          }
        }
        return obj;
      }));
      
      e.preventDefault();
      return;
    }
    
    if (isDrawing && selectedTool === 'rectangle' && currentRect && drawStartPoint) {
      const rect = e.currentTarget.getBoundingClientRect();
      const canvasScale = zoomLevel / 100;
      const rawX = (e.clientX - rect.left - rect.width / 2) / canvasScale;
      const rawY = (e.clientY - rect.top - rect.height / 2) / canvasScale;
      const currentX = rawX;
      const currentY = rawY;
      const isShiftPressed = e.shiftKey;
      
      // Calculate proper rectangle bounds for any drag direction
      const startX = drawStartPoint.x;
      const startY = drawStartPoint.y;
      
      let width = Math.abs(currentX - startX);
      let height = Math.abs(currentY - startY);
      
      // Apply shift constraint for perfect squares
      if (isShiftPressed) {
        const maxDimension = Math.max(width, height);
        width = maxDimension;
        height = maxDimension;
      }
      
      // Calculate position based on drag direction, keeping start point fixed
      let rectX, rectY;
      if (currentX >= startX) {
        rectX = startX; // Dragging right
      } else {
        rectX = startX - width; // Dragging left
      }
      
      if (currentY >= startY) {
        rectY = startY; // Dragging down
      } else {
        rectY = startY - height; // Dragging up
      }
      
      const updatedRect = {
        ...currentRect,
        x: rectX,
        y: rectY,
        width: width,
        height: height
      };
      
      setCurrentRect(updatedRect);
      console.log('Drawing rectangle:', updatedRect, 'Shift pressed:', isShiftPressed);
      e.preventDefault();
      return;
    }
    
    if (isDrawing && selectedTool === 'ellipse' && currentEllipse && drawStartPoint) {
      const rect = e.currentTarget.getBoundingClientRect();
      const canvasScale = zoomLevel / 100;
      const rawX = (e.clientX - rect.left - rect.width / 2) / canvasScale;
      const rawY = (e.clientY - rect.top - rect.height / 2) / canvasScale;
      const currentX = rawX;
      const currentY = rawY;
      const isShiftPressed = e.shiftKey;
      
      // Calculate ellipse bounds - keeping start point as anchor
      const startX = drawStartPoint.x;
      const startY = drawStartPoint.y;
      
      let width = Math.abs(currentX - startX);
      let height = Math.abs(currentY - startY);
      
      // Apply shift constraint for perfect circles
      if (isShiftPressed) {
        const maxDimension = Math.max(width, height);
        width = maxDimension;
        height = maxDimension;
      }
      
      // Calculate center and radii based on drag direction, keeping start point fixed
      let centerX, centerY;
      if (currentX >= startX) {
        centerX = startX + width / 2; // Dragging right
      } else {
        centerX = startX - width / 2; // Dragging left
      }
      
      if (currentY >= startY) {
        centerY = startY + height / 2; // Dragging down
      } else {
        centerY = startY - height / 2; // Dragging up
      }
      
      const updatedEllipse = {
        ...currentEllipse,
        cx: centerX,
        cy: centerY,
        rx: width / 2,
        ry: height / 2
      };
      
      setCurrentEllipse(updatedEllipse);
      console.log('Drawing ellipse:', updatedEllipse, 'Shift pressed:', isShiftPressed);
      e.preventDefault();
      return;
    }
    
    // Handle panning for both hand tool and zoomed select tool
    if (isPanning && (selectedTool === 'hand' || (selectedTool === 'select' && zoomLevel > 100))) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  // Handle double-click events
  const handleDoubleClick = (e) => {
    if (selectedTool === 'select') {
      const rect = e.currentTarget.getBoundingClientRect();
      const canvasScale = zoomLevel / 100;
      const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
      const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
      
      const clickedObject = findClickedObject(x, y);
      if (clickedObject && clickedObject.type === 'text') {
        setEditingTextId(clickedObject.id);
        setTempTextValue(clickedObject.content);
        console.log('Started editing text:', clickedObject);
      }
    }
  };

  const handleMouseUp = () => {
    // Handle hand tool panning end first
    if (isPanning && selectedTool === 'hand') {
      setIsPanning(false);
      setLastPanPoint({ x: 0, y: 0 });
      console.log('Hand tool panning ended');
      return;
    }
    if (isResizing) {
      // Finish resizing - save to history
      setIsResizing(false);
      setResizeHandle(null);
      setResizeStart(null);
      saveToHistory(canvasObjects);
      console.log('Finished resizing object');
      return;
    }
    
    if (isDragging) {
      // Finish dragging - save to history
      setIsDragging(false);
      setDragStart(null);
      saveToHistory(canvasObjects);
      console.log('Finished dragging object');
      return;
    }
    
    if (isDrawing && currentRect) {
      // Finalize the rectangle
      console.log('Finalizing rectangle:', currentRect);
      // Allow any size rectangle, even very thin ones
      if (currentRect.width > 0 && currentRect.height > 0) {
        const newObjects = [...canvasObjects, { ...currentRect }];
        setCanvasObjects(newObjects);
        saveToHistory(newObjects);
        setSelectedObjectId(currentRect.id); // Auto-select the new rectangle
        console.log('Canvas objects after adding:', newObjects);
      }
      setIsDrawing(false);
      setCurrentRect(null);
      setDrawStartPoint(null);
      return;
    }
    
    if (isDrawing && currentEllipse) {
      // Finalize the ellipse
      console.log('Finalizing ellipse:', currentEllipse);
      // Allow any size ellipse, even very small ones
      if (currentEllipse.rx > 0 && currentEllipse.ry > 0) {
        const newObjects = [...canvasObjects, { ...currentEllipse }];
        setCanvasObjects(newObjects);
        saveToHistory(newObjects);
        setSelectedObjectId(currentEllipse.id); // Auto-select the new ellipse
        console.log('Canvas objects after adding:', newObjects);
      }
      setIsDrawing(false);
      setCurrentEllipse(null);
      setDrawStartPoint(null);
      return;
    }
    
    setIsPanning(false);
  };

  // Trackpad/wheel handling for panning and pinch-zoom
  const handleWheel = (e) => {
    // Prevent default scrolling and zooming
    e.preventDefault();
    
    // Handle pinch-to-zoom (ctrlKey is set during pinch gestures on Mac)
    if (e.ctrlKey) {
      // This is a pinch gesture
      const zoomDelta = -e.deltaY;
      
      // Accumulate small pinch movements to reduce sensitivity
      const newAccumulator = pinchAccumulator + zoomDelta;
      setPinchAccumulator(newAccumulator);
      
      // Only change zoom level when accumulator reaches threshold
      const threshold = 85; // Adjusted for 15% more sensitivity (was 100)
      
      if (Math.abs(newAccumulator) >= threshold) {
        const currentIndex = zoomLevels.indexOf(zoomLevel);
        let newZoomLevel;
        
        if (newAccumulator > 0) {
          // Zoom in
          if (currentIndex < zoomLevels.length - 1) {
            newZoomLevel = zoomLevels[currentIndex + 1];
          } else {
            newZoomLevel = zoomLevel;
          }
        } else {
          // Zoom out
          if (currentIndex > 0) {
            newZoomLevel = zoomLevels[currentIndex - 1];
          } else {
            newZoomLevel = zoomLevel;
          }
        }
        
        if (newZoomLevel !== zoomLevel) {
          handleZoomChange(newZoomLevel);
          setPinchAccumulator(0); // Reset accumulator after zoom change
        }
      }
      
      return;
    }
    
    // Check if it's a trackpad gesture for panning (usually has smaller delta values)
    const isTrackpad = Math.abs(e.deltaY) < 50;
    
    if (isTrackpad || e.shiftKey) {
      // Pan with trackpad or shift+scroll
      setPanOffset(prev => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY
      }));
    }
  };

  // Add mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isPanning) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isPanning, lastPanPoint, selectedTool, isDragging, isResizing, isDrawing]);

  return (
    <div className="app-container bg-gray-900">
      <TopNavigation 
        allPanelsOpen={allPanelsOpen} 
        toggleAllPanels={toggleAllPanels}
        onCut={handleCut}
        onCopy={handleCopy}
        onPaste={handlePaste}
        onDelete={handleDelete}
        onUndo={handleUndo}
        onRedo={handleRedo}
        hasSelection={!!selectedObjectId}
        hasClipboard={!!clipboardObject}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
      />
      
      <div className="app-content">
        <LeftToolbar 
          selectedTool={selectedTool}
          onToolSelect={handleToolSelect}
        />
        
        <div 
          className="canvas-area flex flex-col items-center justify-center relative" 
          style={{ backgroundColor: '#5e5e5e' }}
          onClick={(e) => {
            // If clicking on the canvas area background (not the canvas itself), deselect objects
            if (e.target === e.currentTarget) {
              setSelectedObjectId(null);
              console.log('Clicked outside canvas - deselected objects');
            }
          }}
        >
          {/* Invisible mouse capture area extending beyond canvas edges */}
          <div
            className="absolute"
            style={{
              width: '840px',  // 40px wider (20px each side)
              height: '640px', // 40px taller (20px top/bottom)
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: getCurrentCursor(),
              pointerEvents: 'auto'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onDoubleClick={handleDoubleClick}
            onWheel={handleWheel}
          />
          
          <div 
            className="bg-white shadow-2xl mb-4 transition-transform duration-200 ease-out origin-center select-none relative"
            style={{ 
              width: '800px', 
              height: '600px',
              transform: `scale(${canvasScale}) translate(${panOffset.x / canvasScale}px, ${panOffset.y / canvasScale}px)`,
              transformOrigin: 'center center',
              pointerEvents: 'none'  // Let the invisible overlay handle mouse events
            }}
          >
            {/* Canvas content */}
            <div className="w-full h-full flex items-center justify-center text-gray-400 relative">
              {/* Empty canvas - no startup text */}
              
              {/* Render canvas objects */}
              <svg 
                className="absolute inset-0 w-full h-full"
                viewBox="-400 -300 800 600"
                style={{ overflow: 'visible' }}
                preserveAspectRatio="none"
              >

                {/* Render finalized rectangles and circles */}
                {canvasObjects.map((obj) => {
                  if (obj.type === 'rectangle') {
                    const isSelected = obj.id === selectedObjectId;
                    return (
                      <g key={obj.id}>
                        <rect
                          x={obj.x}
                          y={obj.y}
                          width={obj.width}
                          height={obj.height}
                          fill={obj.fill === 'none' ? 'none' : obj.fill}
                          fillOpacity={obj.fill === 'none' ? undefined : (obj.fillOpacity || 1)}
                          stroke={obj.strokeWidth === 0 ? 'none' : obj.stroke}
                          strokeWidth={obj.strokeWidth || 0}
                          strokeOpacity={obj.strokeOpacity || 1}
                          strokeDasharray={obj.strokeStyle === 'dashed' ? '5,5' : obj.strokeStyle === 'dotted' ? '2,2' : 'none'}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Selection handles */}
                        {isSelected && (
                          <g>
                            {/* Resize handles - 8 control points */}
                            {/* Corner handles */}
                            {/* Top-left */}
                            <rect
                              x={obj.x - (3 / canvasScale)}
                              y={obj.y - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'nw-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('top-left');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: top-left');
                              }}
                            />
                            {/* Top-right */}
                            <rect
                              x={obj.x + obj.width - (3 / canvasScale)}
                              y={obj.y - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ne-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('top-right');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: top-right');
                              }}
                            />
                            {/* Bottom-left */}
                            <rect
                              x={obj.x - (3 / canvasScale)}
                              y={obj.y + obj.height - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ne-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('bottom-left');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: bottom-left');
                              }}
                            />
                            {/* Bottom-right */}
                            <rect
                              x={obj.x + obj.width - (3 / canvasScale)}
                              y={obj.y + obj.height - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'nw-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('bottom-right');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: bottom-right');
                              }}
                            />
                            
                            {/* Edge handles */}
                            {/* Top-center */}
                            <rect
                              x={obj.x + obj.width / 2 - (3 / canvasScale)}
                              y={obj.y - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ns-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('top-center');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: top-center');
                              }}
                            />
                            {/* Bottom-center */}
                            <rect
                              x={obj.x + obj.width / 2 - (3 / canvasScale)}
                              y={obj.y + obj.height - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ns-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('bottom-center');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: bottom-center');
                              }}
                            />
                            {/* Left-center */}
                            <rect
                              x={obj.x - (3 / canvasScale)}
                              y={obj.y + obj.height / 2 - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('left-center');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: left-center');
                              }}
                            />
                            {/* Right-center */}
                            <rect
                              x={obj.x + obj.width - (3 / canvasScale)}
                              y={obj.y + obj.height / 2 - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('right-center');
                                setResizeStart({ x, y, objX: obj.x, objY: obj.y, objWidth: obj.width, objHeight: obj.height });
                                console.log('Direct handle click: right-center');
                              }}
                            />
                          </g>
                        )}
                      </g>
                    );
                  }
                  
                  if (obj.type === 'ellipse') {
                    const isSelected = obj.id === selectedObjectId;
                    return (
                      <g key={obj.id}>
                        <ellipse
                          cx={obj.cx}
                          cy={obj.cy}
                          rx={obj.rx}
                          ry={obj.ry}
                          fill={obj.fill === 'none' ? 'none' : obj.fill}
                          fillOpacity={obj.fill === 'none' ? undefined : (obj.fillOpacity || 1)}
                          stroke={obj.strokeWidth === 0 ? 'none' : obj.stroke}
                          strokeWidth={obj.strokeWidth || 0}
                          strokeOpacity={obj.strokeOpacity || 1}
                          strokeDasharray={obj.strokeStyle === 'dashed' ? '5,5' : obj.strokeStyle === 'dotted' ? '2,2' : 'none'}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Selection handles for ellipses */}
                        {isSelected && (
                          <g>
                            {/* Corner handles */}
                            {/* Top-left */}
                            <rect
                              x={obj.cx - obj.rx - (3 / canvasScale)}
                              y={obj.cy - obj.ry - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'nw-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('top-left');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: top-left');
                              }}
                            />
                            {/* Top-right */}
                            <rect
                              x={obj.cx + obj.rx - (3 / canvasScale)}
                              y={obj.cy - obj.ry - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ne-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('top-right');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: top-right');
                              }}
                            />
                            {/* Bottom-left */}
                            <rect
                              x={obj.cx - obj.rx - (3 / canvasScale)}
                              y={obj.cy + obj.ry - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ne-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('bottom-left');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: bottom-left');
                              }}
                            />
                            {/* Bottom-right */}
                            <rect
                              x={obj.cx + obj.rx - (3 / canvasScale)}
                              y={obj.cy + obj.ry - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'nw-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('bottom-right');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: bottom-right');
                              }}
                            />
                            
                            {/* Edge handles */}
                            {/* Top-center */}
                            <rect
                              x={obj.cx - (3 / canvasScale)}
                              y={obj.cy - obj.ry - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ns-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('top-center');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: top-center');
                              }}
                            />
                            {/* Bottom-center */}
                            <rect
                              x={obj.cx - (3 / canvasScale)}
                              y={obj.cy + obj.ry - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ns-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('bottom-center');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: bottom-center');
                              }}
                            />
                            {/* Left-center */}
                            <rect
                              x={obj.cx - obj.rx - (3 / canvasScale)}
                              y={obj.cy - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('left-center');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: left-center');
                              }}
                            />
                            {/* Right-center */}
                            <rect
                              x={obj.cx + obj.rx - (3 / canvasScale)}
                              y={obj.cy - (3 / canvasScale)}
                              width={6 / canvasScale}
                              height={6 / canvasScale}
                              fill="white"
                              stroke="#0066ff"
                              strokeWidth={1 / canvasScale}
                              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                                const canvasScale = zoomLevel / 100;
                                const x = (e.clientX - rect.left - rect.width / 2) / canvasScale;
                                const y = (e.clientY - rect.top - rect.height / 2) / canvasScale;
                                setIsResizing(true);
                                setResizeHandle('right-center');
                                setResizeStart({ x, y, objX: obj.cx, objY: obj.cy, objWidth: obj.rx * 2, objHeight: obj.ry * 2 });
                                console.log('Direct ellipse handle click: right-center');
                              }}
                            />
                          </g>
                        )}
                      </g>
                    );
                  }
                  
                  if (obj.type === 'text') {
                    const isSelected = obj.id === selectedObjectId;
                    const isEditing = obj.id === editingTextId;
                    
                    return (
                      <g key={obj.id}>
                        {isEditing ? (
                          // Render editing input
                          <foreignObject
                            x={obj.x}
                            y={obj.y - obj.fontSize}
                            width="200"
                            height={obj.fontSize + 10}
                          >
                            <input
                              type="text"
                              value={tempTextValue}
                              onChange={(e) => setTempTextValue(e.target.value)}
                              onBlur={() => {
                                // Finish editing
                                const newObjects = canvasObjects.map(o => 
                                  o.id === obj.id ? { ...o, content: tempTextValue } : o
                                );
                                setCanvasObjects(newObjects);
                                setEditingTextId(null);
                                setTempTextValue('');
                                saveToHistory(newObjects);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                                if (e.key === 'Escape') {
                                  setEditingTextId(null);
                                  setTempTextValue('');
                                }
                              }}
                              autoFocus
                              style={{
                                border: 'none',
                                background: 'transparent',
                                fontSize: `${obj.fontSize}px`,
                                fontFamily: obj.fontFamily,
                                color: obj.fill,
                                outline: 'none',
                                width: '100%'
                              }}
                            />
                          </foreignObject>
                        ) : (
                          // Render normal text
                          <text
                            x={obj.x}
                            y={obj.y}
                            fontSize={obj.fontSize}
                            fontFamily={obj.fontFamily}
                            fill={obj.fill}
                            textAnchor={obj.textAnchor}
                            style={{ pointerEvents: 'none' }}
                            onDoubleClick={() => {
                              if (selectedTool === 'select') {
                                setEditingTextId(obj.id);
                                setTempTextValue(obj.content);
                              }
                            }}
                          >
                            {obj.content}
                          </text>
                        )}
                        
                        {/* Selection indicator for text */}
                        {isSelected && !isEditing && (
                          <rect
                            x={obj.x - 2}
                            y={obj.y - obj.fontSize - 2}
                            width={obj.content.length * obj.fontSize * 0.6 + 4}
                            height={obj.fontSize + 4}
                            fill="none"
                            stroke="#0066ff"
                            strokeWidth={1 / canvasScale}
                            strokeDasharray={`${4 / canvasScale},${2 / canvasScale}`}
                            style={{ pointerEvents: 'none' }}
                          />
                        )}
                      </g>
                    );
                  }
                  
                  return null;
                })}
                
                {/* Render current drawing rectangle */}
                {currentRect && (
                  <rect
                    x={currentRect.x}
                    y={currentRect.y}
                    width={currentRect.width}
                    height={currentRect.height}
                    fill={currentRect.fill === 'none' ? 'none' : currentRect.fill}
                    fillOpacity={currentRect.fill === 'none' ? undefined : ((currentRect.fillOpacity || 1) * 0.8)}
                    stroke={currentRect.strokeWidth === 0 ? 'none' : currentRect.stroke}
                    strokeWidth={currentRect.strokeWidth || 0}
                    strokeOpacity={(currentRect.strokeOpacity || 1) * 0.8}
                    strokeDasharray={currentRect.strokeStyle === 'dashed' ? '5,5' : currentRect.strokeStyle === 'dotted' ? '2,2' : 'none'}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
                
                {/* Render current drawing ellipse */}
                {currentEllipse && (
                  <ellipse
                    cx={currentEllipse.cx}
                    cy={currentEllipse.cy}
                    rx={currentEllipse.rx}
                    ry={currentEllipse.ry}
                    fill={currentEllipse.fill === 'none' ? 'none' : currentEllipse.fill}
                    fillOpacity={currentEllipse.fill === 'none' ? undefined : ((currentEllipse.fillOpacity || 1) * 0.8)}
                    stroke={currentEllipse.strokeWidth === 0 ? 'none' : currentEllipse.stroke}
                    strokeWidth={currentEllipse.strokeWidth || 0}
                    strokeOpacity={(currentEllipse.strokeOpacity || 1) * 0.8}
                    strokeDasharray={currentEllipse.strokeStyle === 'dashed' ? '5,5' : currentEllipse.strokeStyle === 'dotted' ? '2,2' : 'none'}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </svg>
            </div>
          </div>
          
          {/* Zoom Control positioned below canvas */}
          <div className="absolute" style={{ bottom: '20px' }}>
            <ZoomControl 
              zoomLevel={zoomLevel}
              onZoomChange={handleZoomChange}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
            />
          </div>
        </div>
        
        <ToolsSidebar 
          allPanelsOpen={allPanelsOpen} 
          toggleAllPanels={toggleAllPanels}
          fillColor={fillColor}
          fillOpacity={fillOpacity}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeStyle={strokeStyle}
          onColorChange={handleColorChange}
          onStrokeChange={handleStrokeChange}
        />
      </div>
      
      {/* Canvas size indicator - positioned at bottom right against right rail */}
      <div 
        className="absolute text-white text-xs font-mono"
        style={{ 
          bottom: '0px', // Flush with bottom edge
          right: '240px', // Right at right sidebar edge (240px wide)
          backgroundColor: '#323232', // Match top navigation bar
          padding: '4px 8px',
          zIndex: 1000 // High z-index to stay on top
        }}
      >
        800 × 600 PX
      </div>
    </div>
  );
};

export default App;
